/**
 * cms-contracts.ts
 *
 * Canonical Domain Contracts for the Headless CMS Engine (Sprint CMS-EDGE-01):
 * - Versioned discriminated union block schemas with migration adapters.
 * - Bounded CmsPageTree and CmsPageSection containers.
 * - Construct-level PublicProductProjection (zero cost, supplier, or clerk PIN leakage).
 * - Exact RFC 8785 signed, expiring LandingPageEdgeBundle and ActiveEdgeReleasePointer.
 * - Authenticated KioskOrderIntentResult and Lead Ingress models.
 */

export type SupportedLocale = 'en-US' | 'sw-KE' | 'lg-UG' | 'fr-FR' | 'ar-SA' | 'zh-CN';


export interface CmsPublicMediaRef {
  readonly mediaId: string;
  readonly publicUrl: string;
  readonly altText: string;
  readonly width: number;
  readonly height: number;
  readonly contentHashSha256: string;
  readonly mimeType: 'image/jpeg' | 'image/png' | 'image/webp' | 'image/avif' | 'image/svg+xml';
  readonly sourceKind?: 'tenant_owned' | 'licensed_stock' | 'ai_generated';
  readonly licenseSummary?: string;
  readonly focalPoint?: {
    readonly x: number;
    readonly y: number;
  };
  readonly responsiveVariants?: readonly {
    readonly width: number;
    readonly url: string;
    readonly contentHashSha256: string;
  }[];
}

export interface CmsBlockMediaAttachment {
  readonly attachmentId: string;
  readonly role: 'site_logo' | 'hero_background' | 'pillar_illustration' | 'soc_diagram';
  readonly media: CmsPublicMediaRef;
  readonly focalPoint?: {
    readonly x: number;
    readonly y: number;
  };
  readonly decorative: boolean;
  readonly localizedAltText: Readonly<Record<SupportedLocale, string>>;
}

export interface CmsPagePresentationConfig {
  readonly defaultThemeId: string;
  readonly allowedThemeIds: readonly string[];
  readonly defaultLocale: SupportedLocale;
  readonly publishedLocales: readonly SupportedLocale[];
  readonly responsivePolicy: 'fluid' | 'fixed-preview';
}

export interface CmsStudioPreviewState {
  readonly themeId: string;
  readonly locale: SupportedLocale;
  readonly viewport: 'desktop' | 'tablet' | 'mobile';
  readonly mode: 'draft' | 'published';
}

export interface CmsBlock<T = unknown> {
  readonly blockId: string;
  readonly type: string;
  readonly payload: T;
  readonly mediaAttachments?: readonly CmsBlockMediaAttachment[];
}

export interface CmsPage {
  readonly pageId: string;
  readonly title: string;
  readonly slug: string;
  readonly revision: number;
  readonly presentation: CmsPagePresentationConfig;
  readonly blocks: readonly CmsBlock[];
  readonly status: 'draft' | 'approved' | 'published' | 'archived';
  readonly updatedAtIso: string;
}

/**
 * Validates media attachments, preventing raw data URIs and unverified external trackers.
 */
export function validateMediaAttachment(attachment: CmsBlockMediaAttachment): { isValid: boolean; error?: string } {
  if (!attachment.media) {
    return { isValid: false, error: 'Media reference is missing.' };
  }

  const { publicUrl, contentHashSha256, mimeType } = attachment.media;

  if (publicUrl.startsWith('data:')) {
    return { isValid: false, error: 'Raw base64 data URIs are forbidden. Assets must be ingested via MediaAssetService.' };
  }

  if (!/^[a-f0-9]{64}$/i.test(contentHashSha256)) {
    return { isValid: false, error: 'Invalid contentHashSha256 digest on public media reference.' };
  }

  const allowedMimeTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/avif', 'image/svg+xml'];
  if (!allowedMimeTypes.includes(mimeType)) {
    return { isValid: false, error: `Disallowed media MIME type: ${mimeType}` };
  }

  if (!attachment.decorative && (!attachment.localizedAltText || !attachment.localizedAltText['en-US'])) {
    return { isValid: false, error: 'Non-decorative media must have localized alt text for at least en-US.' };
  }

  return { isValid: true };
}

export interface CmsSafeLink {
  readonly label: string;
  readonly url: string;
  readonly target: '_self' | '_blank';
  readonly isExternal: boolean;
  readonly rel?: 'noopener noreferrer' | 'nofollow noopener noreferrer';
}



export type CmsBlockType =
  | 'hero_banner'
  | 'product_grid'
  | 'lead_capture_form'
  | 'feature_highlights'
  | 'promotional_ribbon'
  | 'faq_accordion'
  | 'comparison_matrix'
  | 'service_catalog'
  | 'tech_engine'
  | 'industry_matrix'
  | 'trust_audit';

export interface CmsBlockBase {
  readonly blockId: string;
  readonly type: CmsBlockType;
  readonly schemaVersion: number;
  readonly sortOrder: number;
  readonly isVisible: boolean;
}

export interface CmsHeroBannerBlock extends CmsBlockBase {
  readonly type: 'hero_banner';
  readonly schemaVersion: 1;
  readonly payload: {
    readonly eyebrow?: string;
    readonly title: string;
    readonly description?: string;
    readonly primaryCta?: CmsSafeLink;
    readonly secondaryCta?: CmsSafeLink;
    readonly backgroundMediaRef?: string;
    readonly imageUrl?: string;
    readonly imageAltText?: string;
    readonly heroMediaAttachment?: CmsBlockMediaAttachment;
    readonly overlay: 'none' | 'light' | 'dark';
    readonly alignment: 'left' | 'center';
    readonly trustBadges?: readonly string[];
  };
}

export interface CmsComparisonMatrixBlock extends CmsBlockBase {
  readonly type: 'comparison_matrix';
  readonly schemaVersion: 1;
  readonly payload: {
    readonly sectionHeading: string;
    readonly sectionSubheading?: string;
    readonly legacyTitle: string;
    readonly legacyPoints: readonly string[];
    readonly modernTitle: string;
    readonly modernPoints: readonly string[];
    readonly imageUrl?: string;
  };
}

export interface CmsServicePillarItem {
  readonly serviceId: string;
  readonly serviceTitle: string;
  readonly categorySlug: string;
  readonly targetAudience: string;
  readonly shortDescription: string;
  readonly featureList: readonly string[];
  readonly iconName?: string;
  readonly imageUrl?: string;
  readonly imageAltText?: string;
  readonly mediaAttachment?: CmsBlockMediaAttachment;
}

export interface CmsServiceCatalogBlock extends CmsBlockBase {
  readonly type: 'service_catalog';
  readonly schemaVersion: 1;
  readonly payload: {
    readonly sectionHeading?: string;
    readonly sectionSubheading?: string;
    readonly displayMode: 'tabs' | 'grid' | 'accordion';
    readonly services: readonly CmsServicePillarItem[];
  };
}

export interface CmsTechEngineItem {
  readonly featureTitle: string;
  readonly techType: 'soc' | 'video_ai' | 'gps' | 'biometric' | 'audit';
  readonly description: string;
  readonly metricBadge?: string;
  readonly bulletPoints: readonly string[];
  readonly imageUrl?: string;
  readonly imageAltText?: string;
  readonly mediaAttachment?: CmsBlockMediaAttachment;
}


export interface CmsTechEngineBlock extends CmsBlockBase {
  readonly type: 'tech_engine';
  readonly schemaVersion: 1;
  readonly payload: {
    readonly sectionHeading: string;
    readonly coreMessage: string;
    readonly technologies: readonly CmsTechEngineItem[];
  };
}

export interface CmsIndustryItem {
  readonly industryName: string;
  readonly slug: string;
  readonly iconName?: string;
  readonly riskProfileSummary: string;
  readonly applicableServices: readonly string[];
}

export interface CmsIndustryMatrixBlock extends CmsBlockBase {
  readonly type: 'industry_matrix';
  readonly schemaVersion: 1;
  readonly payload: {
    readonly sectionHeading: string;
    readonly sectionSubheading?: string;
    readonly industries: readonly CmsIndustryItem[];
  };
}

export interface CmsTrustAuditItem {
  readonly pillarTitle: string;
  readonly description: string;
  readonly verificationStandard: string;
  readonly title?: string;
  readonly standardBadge?: string;
}

export interface CmsTrustAuditBlock extends CmsBlockBase {
  readonly type: 'trust_audit';
  readonly schemaVersion: 1;
  readonly payload: {
    readonly headline: string;
    readonly body: string;
    readonly sectionHeading?: string;
    readonly sectionSubheading?: string;
    readonly pillars: readonly CmsTrustAuditItem[];
  };
}

export interface CmsProductGridBlock extends CmsBlockBase {
  readonly type: 'product_grid';
  readonly schemaVersion: 1;
  readonly payload: {
    readonly heading?: string;
    readonly selection:
      | { readonly mode: 'manual'; readonly productIds: readonly string[] }
      | { readonly mode: 'collection'; readonly collectionId: string };
    readonly layout: '2-column' | '3-column' | '4-column';
    readonly maximumItems: number;
    readonly showPrices: boolean;
    readonly showAvailability: boolean;
  };
}

export interface CmsLeadCaptureFormBlock extends CmsBlockBase {
  readonly type: 'lead_capture_form';
  readonly schemaVersion: 1;
  readonly payload: {
    readonly formId: string;
    readonly heading: string;
    readonly headline?: string;
    readonly description?: string;
    readonly subheadline?: string;
    readonly allowedFields: readonly ('name' | 'email' | 'phone' | 'organization' | 'message' | 'area_of_inquiry')[];
    readonly sectorDropdownOptions?: readonly string[];
    readonly purpose: 'newsletter' | 'sales_inquiry' | 'support_request' | 'institutional_assessment';
    readonly consentPolicy: {
      readonly required: boolean;
      readonly legalBasis: 'explicit_consent' | 'contractual_necessity';
      readonly policyVersion: string;
      readonly disclosureText: string;
      readonly doubleOptInRequired: boolean;
    };
    readonly privacyNotice?: string;
    readonly submitButtonLabel: string;
    readonly successMessage: string;
    readonly formFields?: readonly {
      readonly fieldId: string;
      readonly label: string;
      readonly fieldType: string;
      readonly isRequired: boolean;
      readonly placeholder?: string;
      readonly options?: readonly string[];
    }[];
    readonly officeDetails?: {
      readonly location: string;
      readonly inquiries: string;
      readonly operationsHours: string;
    };
    readonly officeLocations?: readonly {
      readonly city: string;
      readonly address: string;
      readonly contactEmail?: string;
      readonly phone?: string;
      readonly operationalHours?: string;
    }[];
  };
}


export interface CmsFeatureHighlightsBlock extends CmsBlockBase {
  readonly type: 'feature_highlights';
  readonly schemaVersion: 1;
  readonly payload: {
    readonly heading?: string;
    readonly features: readonly {
      readonly title: string;
      readonly description: string;
      readonly iconName?: string;
    }[];
  };
}

export interface CmsPromotionalRibbonBlock extends CmsBlockBase {
  readonly type: 'promotional_ribbon';
  readonly schemaVersion: 1;
  readonly payload: {
    readonly messageText: string;
    readonly linkUrl?: string;
    readonly isDismissible: boolean;
  };
}

export interface CmsFaqAccordionBlock extends CmsBlockBase {
  readonly type: 'faq_accordion';
  readonly schemaVersion: 1;
  readonly payload: {
    readonly heading?: string;
    readonly items: readonly { readonly question: string; readonly answer: string }[];
  };
}

export type CmsContentBlock =
  | CmsHeroBannerBlock
  | CmsComparisonMatrixBlock
  | CmsServiceCatalogBlock
  | CmsTechEngineBlock
  | CmsIndustryMatrixBlock
  | CmsTrustAuditBlock
  | CmsProductGridBlock
  | CmsLeadCaptureFormBlock
  | CmsFeatureHighlightsBlock
  | CmsPromotionalRibbonBlock
  | CmsFaqAccordionBlock;

export interface CmsBlockMigration {
  readonly blockType: CmsBlockType;
  readonly fromVersion: number;
  readonly toVersion: number;
  migrate(block: Record<string, unknown>): CmsContentBlock;
}

export interface CmsPageSection {
  readonly sectionId: string;
  readonly layout: 'full_width' | 'contained' | 'two_column' | 'three_column';
  readonly themeVariant?: 'default' | 'muted' | 'inverse' | 'accent';
  readonly blocks: readonly CmsContentBlock[];
}

export interface CmsPageTree {
  readonly rootVersion: number;
  readonly sections: readonly CmsPageSection[];
}

export interface PublicMediaRef {
  readonly mediaId: string;
  readonly publicUrl: string;
  readonly altText: string;
  readonly width?: number;
  readonly height?: number;
}

export interface PublicProductProjection {
  readonly productId: string;
  readonly sku: string;
  readonly title: string;
  readonly slug: string;
  readonly shortDescription?: string;
  readonly publicPrice?: {
    readonly amountMinor: number;
    readonly currency: string;
  };
  readonly publicAvailability: 'available' | 'limited' | 'unavailable';
  readonly media: readonly PublicMediaRef[];
  readonly badges: readonly string[];
  readonly catalogRevision: string;
}

export interface BundleFreshnessPolicy {
  readonly maxAgeSeconds: number;
  readonly staleGraceSeconds: number;
  readonly allowStaticReadDuringGrace: boolean;
  readonly allowLeadCaptureDuringGrace: boolean;
  readonly allowKioskOrderIntentDuringGrace: boolean;
}

export interface LandingPageEdgeBundle {
  readonly bundleVersion: string;
  readonly releaseId: string;
  readonly tenantId: string;
  readonly channelId: string;
  readonly pageId: string;
  readonly pageRevision: number;
  readonly siteTitle: string;
  readonly baseDomain: string;
  readonly pageTree: CmsPageTree;
  readonly themeTokens: Record<string, string>;
  readonly featuredProducts: readonly PublicProductProjection[];
  readonly forms: readonly CmsLeadCaptureFormBlock['payload'][];
  readonly freshnessPolicy: BundleFreshnessPolicy;
  readonly generatedAt: string;
  readonly expiresAt: string;
  readonly canonicalizationAlgorithm: 'RFC8785';
  readonly payloadHashSha256: string;
  readonly signingKeyId: string;
  readonly publicKeySetVersion: string;
  readonly signature: string;
}

export interface ActiveEdgeReleasePointer {
  readonly tenantId: string;
  readonly channelId: string;
  readonly kind: 'landing' | 'kiosk';
  readonly releaseId: string;
  readonly manifestHash: string;
  readonly activatedAt: string;
  readonly previousReleaseId?: string;
  readonly signingKeyId: string;
  readonly signature: string;
}

export interface PublicCartLine {
  readonly productId: string;
  readonly sku: string;
  readonly quantity: number;
  readonly unitPriceMinor: number;
}

export interface KioskOrderIntentEnvelope {
  readonly intentId: string;
  readonly kioskDeviceId: string;
  readonly tenantId: string;
  readonly channelId: string;
  readonly sessionId: string;
  readonly sessionNonce: string;
  readonly catalogRevision: string;
  readonly cart: readonly PublicCartLine[];
  readonly createdAt: string;
  readonly expiresAt: string;
  readonly idempotencyKey: string;
  readonly deviceSignature: string;
}

export type KioskOrderIntentResult =
  | { readonly status: 'hold_created'; readonly claimToken: string; readonly expiresAt: string; readonly holdReference: string }
  | { readonly status: 'already_processed'; readonly claimToken: string; readonly expiresAt: string; readonly holdReference: string }
  | { readonly status: 'session_expired'; readonly reason: string }
  | { readonly status: 'catalog_refresh_required'; readonly currentCatalogRevision: string }
  | { readonly status: 'stock_unavailable'; readonly unavailableSkus: readonly string[] }
  | { readonly status: 'temporarily_unavailable'; readonly reason: string };
